import type {RefObject} from 'react';
import {useCallback} from 'react';

import {useFitMeasurement} from './useFitMeasurement';

// Must match the gap-x1 (8px) between items on the overflow row.
const OVERFLOW_GAP_PX = 8;

/**
 * Longest item prefix that fits the row's first line while leaving room for the
 * trailing control (the row's last child) right after it. Offsets are
 * row-relative (the row is position:relative). Returns Infinity when nothing
 * wraps — including when layout isn't available (happy-dom/SSR report every
 * offset as 0), which keeps every item visible (content-complete fallback).
 *
 * This is TagCategoryFilterBar's proven `measureFit`, lifted into the library.
 */
const measureVisibleCount = (row: HTMLDivElement | null): number => {
  if (!row) {
    return Number.POSITIVE_INFINITY;
  }
  const children = Array.from(row.children) as HTMLElement[];
  const control = children[children.length - 1];
  const items = children.slice(0, -1);
  if (!control || items.length === 0) {
    return Number.POSITIVE_INFINITY;
  }
  const firstLineTop = items[0].offsetTop;
  if (control.offsetTop === firstLineTop) {
    return Number.POSITIVE_INFINITY;
  }
  // An item stays only if the control would still fit on the line after it.
  const limit = row.clientWidth - control.offsetWidth - OVERFLOW_GAP_PX;
  let count = 0;
  for (const item of items) {
    if (item.offsetTop !== firstLineTop || item.offsetLeft + item.offsetWidth > limit) {
      break;
    }
    count += 1;
  }
  return count;
};

export type OverflowCountResult = {
  /** Attach to the row element — the width constraint and ResizeObserver target. */
  rowRef: RefObject<HTMLDivElement | null>;
  /**
   * How many leading items fit on one line while reserving room for the trailing
   * control. `Infinity` means all items fit (and while measuring / with no
   * layout available). Slice your item list to this count.
   */
  visibleCount: number;
};

/**
 * Headless engine for a responsive "as many as fit + trailing control" row (the
 * count-overflow / "priority+" pattern — e.g. filter chips overflowing into a
 * "Show all" menu). Render every item plus the control as the row's children,
 * with the control LAST; this reports how many leading items fit. Re-measures on
 * a ResizeObserver and whenever `remeasureKey` changes.
 */
export const useOverflowCount = ({
  remeasureKey
}: {remeasureKey?: string | number} = {}): OverflowCountResult => {
  const measure = useCallback((row: HTMLDivElement | null) => measureVisibleCount(row), []);
  const {rowRef, value} = useFitMeasurement<number>(measure, remeasureKey);
  return {rowRef, visibleCount: value === null ? Number.POSITIVE_INFINITY : value};
};
